import React from 'react';
import { Handle, Position } from 'reactflow';

interface BaseNodeProps {
  /** Whether this node is selected (adds outline ring) */
  selected?: boolean;
  /** Gradient background classes for the node */
  bgClasses: string;
  /** Ring color when selected */
  ringColor: string;
  /** Optional header content (node title) */
  header?: React.ReactNode;
  /** Main content area of the node */
  children: React.ReactNode;
  /** Footer slot for action buttons */
  footer?: React.ReactNode;
}

/**
 * BaseNode provides a consistent container for all policy graph nodes:
 * - Positions React Flow Handles
 * - Applies uniform sizing, padding, and background
 * - Anchors a fixed footer for icons
 * - Houses a scrollable content area
 */
export const BaseNode: React.FC<BaseNodeProps> = ({
  selected = false,
  bgClasses,
  ringColor,
  header,
  children,
  footer
}) => {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={`relative flex flex-col items-center w-full h-full pt-3 px-3 pb-12 rounded-lg shadow-md bg-gradient-to-br ${bgClasses} ${
        selected ? `ring-2 ${ringColor}` : ''
      }`}
    >
      {/* Top handle for incoming edges */}
      <Handle type="target" position={Position.Top} className="!bg-gray-500" />

      {/* Optional header slot */}
      {header && (
        <div className="w-full mb-2 text-center text-sm font-bold uppercase text-gray-700 dark:text-gray-300">
          {header}
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 w-full">
        {children}
      </div>

      {/* Footer slot positioned absolutely at bottom */}
      {footer && (
        <div className="absolute bottom-2 left-2 right-2 flex flex-wrap justify-center gap-1 py-1">
          {footer}
        </div>
      )}

      {/* Bottom handle for outgoing edges */}
      <Handle type="source" position={Position.Bottom} className="!bg-gray-500" />
    </div>
  );
};

BaseNode.displayName = 'BaseNode'; 